import React, { useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Chip,
    Box,
    Stack,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    LinearProgress,
    useTheme
} from "@mui/material";
import {
    MoreVert as MoreVertIcon,
    ThumbUp as ThumbUpIcon,
    ThumbsUpDown as ThumbsUpDownIcon,
    ThumbDownAlt as ThumbDownAltIcon,
    StarBorder as StarBorderIcon,
    Star as StarIcon,
    CheckCircle as CheckCircleIcon,
    AccessTime as AccessTimeIcon,
    Rule as RuleIcon,
    Payments as PaymentsIcon,
    LocationOnOutlined as LocationOnOutlinedIcon,
} from "@mui/icons-material";

import { SegmentedProgressBar } from "../../../shared/components";
import { ProjectCardProps } from "../types";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { clientTheme } from "../../../theme/clientTheme";

const cardVariant = clientTheme.layout?.projectCardVariant ?? "classic";
const stageColors = clientTheme.layout?.stageChipColors ?? {};

export const ProjectCard: React.FC<ProjectCardProps> = ({
    title,
    location,
    workType,
    workTypeDescription,
    approvalStage,
    progress,
    projectId,
    phases,
    ragStatus,
    isPinned,
    handleClick,
    handlePinToggle,
}) => {

    const theme = useTheme();
    const { isAllowedToViewProject, isAllowedToPinProject } = useCapabilities();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => setAnchorEl(null);

    if (cardVariant === "modern") {
        const stageColor = stageColors[approvalStage];
        return (
            <Card
                variant="outlined"
                sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.08)",
                    cursor: isAllowedToViewProject ? "pointer" : "default",
                }}
                onClick={isAllowedToViewProject ? handleClick : undefined}
            >
                <CardContent sx={{ px: 2.5, pt: 2.5, pb: 2, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                                lineHeight: 1.35,
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {title}
                        </Typography>
                        {isAllowedToPinProject && (
                            <IconButton size="small" onClick={handleMenuOpen} sx={{ mt: -0.5, flexShrink: 0 }}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MenuItem onClick={() => { handleMenuClose(); handlePinToggle(!isPinned); }}>
                            {isPinned ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <StarIcon sx={{ color: "primary.main" }} fontSize="small" />
                                    <Typography variant="subtitle2">Unpin Project</Typography>
                                </Box>
                            ) : (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <StarBorderIcon sx={{ color: "primary.main" }} fontSize="small" />
                                    <Typography variant="subtitle2">Pin Project</Typography>
                                </Box>
                            )}
                        </MenuItem>
                    </Menu>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <LocationOnOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                        >
                            {location || "Location not specified"}
                        </Typography>
                    </Stack>

                    <Box>
                        <Chip
                            label={approvalStage}
                            size="small"
                            sx={{
                                borderRadius: "999px",
                                fontWeight: 500,
                                bgcolor: stageColor?.bg ?? "action.hover",
                                color: stageColor?.text ?? "text.primary",
                                border: "none",
                                height: 24,
                                "& .MuiChip-label": { px: 1.5 },
                            }}
                        />
                    </Box>

                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" color="text.secondary">Progress</Typography>
                            <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: "action.hover",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: 3,
                                    bgcolor: "success.main",
                                },
                            }}
                        />
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                        Ref: {projectId}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Classic (Cadent) variant
    return (
        <Card
            variant="outlined"
            sx={{
                borderTopWidth: "4px",
                borderTopStyle: "solid",
                borderTopColor:
                    ragStatus.overall === "green"
                        ? "success.main"
                        : ragStatus.overall === "amber"
                            ? "warning.main"
                            : "error.main",
                height: "100%",
                cursor: isAllowedToViewProject ? "pointer" : "default",
            }}
            onClick={isAllowedToViewProject ? handleClick : undefined}
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography
                        variant="body2"
                        sx={{ maxWidth: "70%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                    >
                        {title}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Tooltip
                            title={
                                <Box sx={{ p: 0.5, py: 1, display: "flex", gap: 1, bgcolor: theme.palette.background.default }}>
                                    <CheckCircleIcon sx={{ color: "success.main", fontSize: "1rem" }} />
                                    <Typography variant="body2">
                                        <b>Overall Project RAG:</b> Based on <b>Cost, Time,</b> and <b>Quality</b> performance. Red overrides Amber, Amber overrides Green.
                                    </Typography>
                                </Box>
                            }
                            arrow
                            placement="top-start"
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: theme.palette.background.default,
                                        color: "text.primary",
                                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.32)",
                                        borderRadius: "8px",
                                        maxWidth: "300px",
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: theme.palette.background.default,
                                        fontSize: "1rem",
                                    },
                                },
                            }}
                        >
                            {ragStatus.overall === "green" ? (
                                <ThumbUpIcon sx={{ color: "success.main", fontSize: 20 }} />
                            ) : ragStatus.overall === "amber" ? (
                                <ThumbsUpDownIcon sx={{ color: "warning.main", fontSize: 20 }} />
                            ) : (
                                <ThumbDownAltIcon sx={{ color: "error.main", fontSize: 20 }} />
                            )}
                        </Tooltip>

                        {isAllowedToPinProject && (
                            <IconButton size="small" onClick={handleMenuOpen}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        )}

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MenuItem onClick={() => { handleMenuClose(); handlePinToggle(!isPinned); }}>
                                {isPinned ? (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <StarIcon sx={{ color: "primary.main" }} fontSize="small" />
                                        <Typography variant="subtitle2">Unpin Project</Typography>
                                    </Box>
                                ) : (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <StarBorderIcon sx={{ color: "primary.main" }} fontSize="small" />
                                        <Typography variant="subtitle2">Pin Project</Typography>
                                    </Box>
                                )}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                    {location ? location : "Location not specified"}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ my: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                    {workTypeDescription ? workTypeDescription : "Work type not specified"}
                </Typography>

                <Chip sx={{ borderRadius: 1 }} label={approvalStage} size="small" variant="filled" />

                <Box display="flex" alignItems="center" gap={1} sx={{ my: 1 }}>
                    <SegmentedProgressBar phases={phases} progressPercentage={progress} status={ragStatus.overall} />
                </Box>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{projectId}</Typography>
                    <Stack direction="row" spacing={1} alignItems="stretch">
                        <PaymentsIcon
                            sx={{
                                fontSize: 22,
                                color: ragStatus.cost === "amber" ? "warning.main" : ragStatus.cost === "green" ? "success.main" : "error.main",
                            }}
                        />
                        <AccessTimeIcon
                            sx={{
                                fontSize: 22,
                                color: ragStatus.time === "amber" ? "warning.main" : ragStatus.time === "green" ? "success.main" : "error.main",
                            }}
                        />
                        <RuleIcon
                            sx={{
                                fontSize: 22,
                                color: ragStatus.quality === "amber" ? "warning.main" : ragStatus.quality === "green" ? "success.main" : "error.main",
                            }}
                        />
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};
